/**
 * @file CertificatesTab.js
 * @description Certificates tab component for the Multi-tenant SSO Wizard.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import SigningCertificateDropdown from '../../shared/asyncDropdowns/SigningCertificateDropdown';
import {
    fetchCertificateById,
    createSystemCertificate,
} from '../../../services/ssoService';
import style from '../sso.module.scss';
import Toast from '../../ui/toasts/Toast';
import { getText } from '../../../i18n';

const CertificatesTab = ({ data, onChange }) => {
    const fileInputRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);
    const [formData, setFormData] = useState({
        encryptionCertificateId: data.encryptionCertificateId || null,
        signingCertificateId: data.signingCertificateId || null,
        encryptionCertificate: null,
        signingCertificate: data.signingCertificate || null,
        displayPublicKey: false,
        displayCertificate: false,
    });

    useEffect(() => {
        const fetchEncryptionCertificate = async () => {
            if (formData.encryptionCertificateId) {
                try {
                    const certificate = await fetchCertificateById(
                        formData.encryptionCertificateId,
                    );
                    setFormData(prevData => ({
                        ...prevData,
                        encryptionCertificate: certificate,
                    }));
                } catch (error) {
                    console.error(
                        'Error fetching encryption certificate:',
                        error,
                    );
                }
            }
        };

        fetchEncryptionCertificate();
    }, [formData.encryptionCertificateId]);

    useEffect(() => {
        onChange(formData);
    }, [formData, onChange]);

    const handleCertificateChange = useCallback(
        (field, certificateId, payload) => {
            setFormData(prevData => {
                if (
                    prevData[`${field}Id`] !== certificateId ||
                    !prevData[field]
                ) {
                    return {
                        ...prevData,
                        [`${field}Id`]: certificateId,
                        [field]: payload,
                    };
                }
                return prevData;
            });
        },
        [],
    );

    const handleCheckboxChange = event => {
        const { name, checked } = event.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: checked,
        }));
    };

    const resetFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleUpload = async event => {
        const file = event.target.files[0];
        if (!file) return;

        setIsUploading(true);
        try {
            const certificateData = {
                certificate: file,
                name: file.name,
                purposeId: 1,
            };

            const uploadedCertificate =
                await createSystemCertificate(certificateData);
            setFormData(prevData => ({
                ...prevData,
                encryptionCertificateId: uploadedCertificate.id,
                encryptionCertificate: uploadedCertificate,
            }));
            Toast.success(getText('msg_4788ff73daf8'));
        } catch (error) {
            if (error?.status === 400) {
                Toast.error(
                    getText('msg_3d84b7b7084b'),
                );
            } else {
                Toast.error(getText('msg_985b02204002'));
            }
        } finally {
            setIsUploading(false);
            resetFileInput();
        }
    };

    const handleRemoveCertificate = () => {
        setFormData(prevData => ({
            ...prevData,
            encryptionCertificateId: null,
            encryptionCertificate: null,
        }));
        resetFileInput();
    };

    return (
        <div
            className={`col-12 col-xs-12 col-md-12 col-lg-6 m-auto pb-5 ${style.certificateTab}`}
        >
            <div className="form-group">
                <SigningCertificateDropdown
                    selectedCertificateId={formData.signingCertificateId}
                    onCertificateChange={(id, payload) =>
                        handleCertificateChange(
                            'signingCertificate',
                            id,
                            payload,
                        )
                    }
                    inlineLabel={false}
                    allowNullSelection={true}
                    isRequired={true}
                    label={getText('msg_1ebd45fcbe28')}
                />
            </div>

            <hr />

            <div className="form-group relative-position">
                <label htmlFor="encryptionCertificate">{getText('msg_0af149c2ed16')}</label>
                <div className="d-flex align-items-center position-relative">
                    <input
                        type="file"
                        id="encryptionCertificateUpload"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleUpload}
                        accept=".crt,.cer,.txt,.pem"
                        disabled={isUploading}
                    />
                    <label
                        htmlFor="encryptionCertificateUpload"
                        className={`btn btn-primary ing-button-primary-left me-2 mb-0 text text-bg-primary ${
                            isUploading ? 'disabled' : ''
                        }`}
                        style={{ pointerEvents: isUploading ? 'none' : 'auto' }}
                    >
                        <i className="bi bi-upload"></i>
                        {isUploading ? 'Uploading...' : 'Upload'}
                    </label>
                    <input
                        type="text"
                        id="encryptionCertificate"
                        className="form-control"
                        value={
                            formData.encryptionCertificate
                                ? formData.encryptionCertificate.name
                                : ''
                        }
                        placeholder={
                            formData.encryptionCertificateId
                                ? ''
                                : 'No certificate uploaded'
                        }
                        readOnly
                    />
                    {formData.encryptionCertificateId && (
                        <button
                            className={`btn btn-link ${style.deleteButton}`}
                            onClick={handleRemoveCertificate}
                            disabled={isUploading}
                            type="button"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    )}
                </div>
                {formData.encryptionCertificate && (
                    <small className={`text-danger ${style.certDate}`}>
                        <span className="me-1">{getText('msg_fbab3a4242e6')}</span>
                        <span>
                            {new Date(
                                formData.encryptionCertificate.expirationDate,
                            ).toLocaleDateString()}
                        </span>
                    </small>
                )}
            </div>

            {formData.encryptionCertificateId && (
                <>
                    <div className="form-check mb-3 pt-5">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="displayPublicKey"
                            name="displayPublicKey"
                            checked={formData.displayPublicKey}
                            onChange={handleCheckboxChange}
                        />
                        <label
                            className="form-check-label"
                            htmlFor="displayPublicKey"
                        >
                            {getText('msg_8b7047c1135a')}
                        </label>
                    </div>

                    {formData.displayPublicKey &&
                        formData.encryptionCertificate && (
                            <div className="mb-3">
                                <textarea
                                    className="form-control"
                                    rows="5"
                                    readOnly
                                    value={
                                        formData.encryptionCertificate
                                            .publicKey || ''
                                    }
                                ></textarea>
                            </div>
                        )}

                    <div className="form-check mb-3">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="displayCertificate"
                            name="displayCertificate"
                            checked={formData.displayCertificate}
                            onChange={handleCheckboxChange}
                        />
                        <label
                            className="form-check-label"
                            htmlFor="displayCertificate"
                        >
                            {getText('msg_ab4b9507a81e')}
                        </label>
                    </div>

                    {formData.displayCertificate &&
                        formData.encryptionCertificate && (
                            <div className="mb-3">
                                <textarea
                                    className="form-control"
                                    rows="5"
                                    readOnly
                                    value={
                                        formData.encryptionCertificate.value ||
                                        ''
                                    }
                                ></textarea>
                            </div>
                        )}
                </>
            )}
        </div>
    );
};

export default CertificatesTab;
